import BigNumber from 'bignumber.js'

type Proto = StringConstructor | NumberConstructor

/**
 * 判断是否为数字, 兼容小数，负数，正数
 * @param value
 * @returns
 */
const isNumber = (value: number | string): boolean => {
  const reg = /^-?\d+(\.\d+)?$/
  return reg.test(value.toString())
}

function formatNumberWithCommas(num: number | string, isOriginMoney = false): string {
  if (num === null || num === undefined || num === '') return ''

  let str = String(num)

  // 如果本身是科学计数法，转成普通小数字符串
  if (/e/i.test(str)) {
    str = Number(str).toFixed(20) // 保留 20 位
    str = str.replace(/0+$/, '').replace(/\.$/, '') // 去掉多余的 0 和小数点
  }

  if (isOriginMoney) {
    const strSplit = `${num}`.split('.')
    const integer = strSplit[0]
    const decimal = strSplit[1] || ''
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimal
  }

  if (num.isGreaterThan(1000000000000)) {
    num = num.divideStr(1000000000).toStringAsFixed(2)
    const parts = num.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return `${parts.join('.')}B`
  } else {
    const isM = num.isGreaterThan(1000000000)
    if (isM) {
      num = num.divideStr(1000000).toStringAsFixed(2)
    }

    const parts = num.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return isM ? `${parts.join('.')}M` : parts.join('.')
  }
}

/**
 * 扩展String和Number的方法
 *
 * @param {Proto} proto
 * @return {void}
 */
const extensionsStringAndNumberMethods = (Proto: Proto): void => {
  class Extension extends Proto implements ExtensionStringAndNumber {
    constructor(value: number | string) {
      super(value)
    }

    /**
     * 处理百分比
     * @param decimal
     * @returns
     */
    formatPercent(decimal: 2) {
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      const prefixStr = value > 0 ? '+' : ''
      return `${prefixStr}${BigNumber(this.toString()).toFixed(decimal)}%`
    }

    multiplyStr(num: number | string) {
      if (!num) {
        num = '0'
      }
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      return BigNumber(this.toString()).times(BigNumber(num)).valueOf()
    }

    divideStr(num?: number | string) {
      if (!num) {
        num = '0'
      }
      if (!isNumber(num)) {
        return ''
      }
      const res = BigNumber(this.toString()).div(BigNumber(num)).valueOf()
      return isNaN(res.toNumber()) ? '' : res
    }

    formatNumberWithCommas(isOriginMoney?: boolean) {
      const temValue = this.valueOf()
      if (typeof temValue === 'string' && temValue.includes(',')) {
        return temValue
      }
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      return formatNumberWithCommas(this.toString(), isOriginMoney)
    }

    subtractStr(num: number | string) {
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      return BigNumber(this.toString()).minus(BigNumber(num)).valueOf()
    }

    addStr(num?: number | string) {
      if (!num) {
        num = '0'
      }
      if (!isNumber(this.toString())) {
        return ''
      }
      return BigNumber(this.toString()).plus(BigNumber(num)).valueOf()
    }

    cutDecimal(decimal = 4) {
      const _value = this.toString()
      if (!isNumber(_value)) {
        return ''
      }
      if (!_value.includes('.') && decimal > 0) {
        return _value.decimalPadZero(decimal)
      }
      const [integer, decimalStr] = _value.split('.')
      if (decimal.isEqualTo(0)) {
        return integer
      }
      if (decimalStr.length < decimal) {
        return _value.decimalPadZero(decimal)
      }
      return `${integer}.${decimalStr.substring(0, decimal)}`
    }

    modeStr(num: number | string) {
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      return BigNumber(this.toString()).modulo(BigNumber(num)).valueOf()
    }

    toNumber() {
      const value = Number(this)
      if (isNaN(value)) {
        return 0
      }
      return BigNumber(this.toString()).toNumber()
    }

    ceil(digit: number = 2) {
      const value = this.toString()

      if (!isNumber(value)) {
        return ''
      }
      return BigNumber(this.toString()).decimalPlaces(digit, BigNumber.ROUND_UP).valueOf()
    }

    toStringAsFixed(digit: number) {
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      return BigNumber(this.toString()).toFixed(digit)
    }

    adjustDecimal(decimal: number = 2) {
      const value = Number(this)
      if (isNaN(value)) {
        return ''
      }
      const originNumber = this.toString()
      const number = BigNumber(originNumber)
      if (number.isGreaterThan(0)) {
        return number.decimalPlaces(decimal, BigNumber.ROUND_DOWN).valueOf()
      }
      if (number.isLessThan(0)) {
        const [integer, originDecimal] = originNumber.split('.')

        if (!originDecimal) return number.toFixed(decimal)
        if (originDecimal.length <= decimal) return originNumber
        const digitValue = `-0.${originDecimal.substring(0, decimal)}`

        return BigNumber(integer)
          .plus(digitValue)
          .minus(BigNumber(1 / Math.pow(10, decimal)))
          .toFixed(decimal)
      }
      return number.decimalPlaces(decimal, BigNumber.ROUND_DOWN).valueOf()
    }

    decimalPadZero(length: number = 2): string {
      let value = `${this.valueOf()}`
      if (!value) value = '0'
      if (!value.includes('.')) {
        return `${value}.${'0'.repeat(length)}`
      }
      const [integer, decimal] = value.split('.')
      return `${integer}.${decimal.padEnd(length, '0')}`
    }

    get removeTrailingZero() {
      let value = `${this.valueOf()}`

      if (!value.includes('.')) {
        return value
      }
      value = value.replace(/0*$/g, '')
      if (value.endsWith('.')) {
        value = value.substring(0, value.length - 1)
      }

      return value
    }

    isGreaterThan(value: string | number) {
      return BigNumber(this.toString()).isGreaterThan(value)
    }

    isLessThan(value: string | number) {
      return BigNumber(this.toString()).isLessThan(value)
    }

    isEqualTo(value: string | number) {
      return BigNumber(this.toString()).isEqualTo(value)
    }

    isGreaterThanOrEqualTo(value: string | number) {
      return BigNumber(this.toString()).isGreaterThanOrEqualTo(value)
    }

    isLessThanOrEqualTo(value: string | number) {
      return BigNumber(this.toString()).isLessThanOrEqualTo(value)
    }

    toFloatNumber() {
      const value = this.toString()
      if (!isNumber(value)) {
        return '0'
      }
      return value
    }

    formatThousandAndMillion(decimal = 2) {
      const value = this.toString()
      if (!isNumber(value)) {
        return '0'
      }
      if (value.isGreaterThan(1000000)) {
        return `${value.divideStr(1000000).toStringAsFixed(decimal)}M`
      }
      if (value.isGreaterThan(1000)) {
        return `${value.divideStr(1000).toStringAsFixed(decimal)}K`
      }
      return value.formatCommonMoney(decimal).cutDecimal(decimal)
    }

    formatCommonMoney(decimal: number = 2) {
      const value = this.toString()
      if (!isNumber(value)) {
        return '0.00'
      }
      return value.formatNumberWithCommas().decimalPadZero(decimal)
    }
  }

  const extensionInstance = Object.getPrototypeOf(new Extension(Proto === Number ? 0 : ''))

  const descriptors = Object.getOwnPropertyDescriptors(extensionInstance)

  for (const [method, descriptor] of Object.entries(descriptors)) {
    if (method in Proto.prototype || method === 'constructor') {
      continue
    }
    if (descriptor.get) {
      Object.defineProperty(Proto.prototype, method, {
        get: descriptor.get,
        configurable: true,
        enumerable: true,
      })
    }
    if (descriptor.set) {
      Object.defineProperty(Proto.prototype, method, {
        set: descriptor.set,
        configurable: true,
        enumerable: true,
      })
    }

    if ('value' in descriptor) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      Proto.prototype[method] = descriptor.value
    }
  }
}

extensionsStringAndNumberMethods(String)
extensionsStringAndNumberMethods(Number)

export const isEmpty = (obj: Record<string, any>): boolean => {
  return Object.keys(obj).length === 0
}
